import {
  Archive,
  Inbox,
  PenLine,
  Plus,
  RefreshCw,
  Send,
  ShieldAlert,
  SquarePen,
  Star,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { Suspense, lazy, useState } from 'react';
import { DropdownItem, DropdownSeparator, cn } from '@waymail/ui';
import type { Pasta } from '../lib/api';
import { ehDeSistema, rotularPastas, type PastaRotulada } from '../lib/pastas';
import { useUi } from '../store/ui';
import { AcoesDaPasta, ItensDaPasta, type PedidoDePasta } from './AcoesDaPasta';
import {
  MenuDeContexto,
  ehTeclaDeMenu,
  pontoDoElemento,
  pontoDoEvento,
  type PontoDoMenu,
} from './MenuDeContexto';

// O dialogo so existe quando alguem pede: fora do bundle inicial.
const DialogoDePasta = lazy(() =>
  import('./DialogoDePasta').then((m) => ({ default: m.DialogoDePasta })),
);
import { BuscasSalvas } from './BuscasSalvas';
import { Acompanhamento } from './Acompanhamento';
import { PastasInteligentes } from './PastasInteligentes';
import { EstadoDasContas } from './EstadoDasContas';
import { LogotipoWayMail } from './Logotipo';

/**
 * Sidebar do print, agora com as pastas que vieram do servidor. Favoritos sao
 * as de papel conhecido; o resto cai em Pastas.
 *
 * Pasta com assinatura cancelada nao aparece, como no Roundcube (ADR 0045) -
 * menos as de sistema, que a API nem deixa esconder. Criar, renomear e
 * excluir abrem os mesmos dialogos da aba Pastas das Configuracoes.
 */

const ICONES: Record<string, LucideIcon> = {
  inbox: Inbox,
  sent: Send,
  drafts: PenLine,
  trash: Trash2,
  archive: Archive,
  junk: ShieldAlert,
};

const FAVORITOS = ['inbox', 'sent', 'drafts', 'trash'];

export interface SidebarProps {
  onCompose: () => void;
  /** Conta das pastas abaixo; `null` enquanto nenhuma carregou. */
  accountId?: string | null;
  pastas: Pasta[];
  carregando: boolean;
  onAdicionarConta: () => void;
}

export function Sidebar({
  onCompose,
  accountId = null,
  pastas,
  carregando,
  onAdicionarConta,
}: SidebarProps) {
  const collapsed = useUi((s) => s.sidebarCollapsed);
  const folderId = useUi((s) => s.folderId);
  const selectFolder = useUi((s) => s.selectFolder);
  const verRascunhos = useUi((s) => s.verRascunhos);
  const inteligenteAberta = useUi((s) => s.pastaInteligente !== null);
  const abrirRascunhos = useUi((s) => s.abrirRascunhos);
  const abrirConfiguracoes = useUi((s) => s.abrirConfiguracoes);
  const [pedido, setPedido] = useState<PedidoDePasta | null>(null);
  const [menu, setMenu] = useState<{ pasta: Pasta; ponto: PontoDoMenu } | null>(null);

  if (collapsed) return null;

  const visiveis = pastas.filter((p) => p.subscribed !== false || ehDeSistema(p));
  // Rotula sobre a lista inteira: a colisao pode ser entre um favorito e uma
  // pasta comum, e so quem ve as duas sabe disso.
  const rotuladas = rotularPastas(visiveis);
  const acoes =
    accountId === null
      ? undefined
      : (p: Pasta) => (
          <AcoesDaPasta
            pasta={p}
            onPedir={setPedido}
            extra={
              <>
                <DropdownSeparator />
                <DropdownItem onSelect={() => abrirConfiguracoes('pastas')}>
                  Gerenciar pastas
                </DropdownItem>
              </>
            }
          />
        );
  const ehFavorita = (p: Pasta) => p.role !== null && FAVORITOS.includes(p.role);
  const favoritos = rotuladas.filter((r) => ehFavorita(r.pasta));
  const demais = rotuladas.filter((r) => !ehFavorita(r.pasta));
  // Rascunhos do servidor (ADR 0052) valem para qualquer caixa. Quando ela nao
  // tem pasta de rascunhos, a entrada e virtual e a pasta atual deixa de ser a ativa.
  const rascunhosVirtual = !visiveis.some((p) => p.role === 'drafts');
  const pastaAtiva = (id: string) =>
    id === folderId && !inteligenteAberta && !(rascunhosVirtual && verRascunhos);

  return (
    <nav
      aria-label="Pastas"
      className="flex w-60 shrink-0 flex-col gap-1 overflow-y-auto bg-app px-4 py-5"
    >
      <div className="mb-7 shrink-0 px-1">
        <LogotipoWayMail className="h-6 w-auto text-rail dark:text-white" />
      </div>

      <button
        type="button"
        onClick={onCompose}
        className="mx-4 mb-7 flex h-12 shrink-0 cursor-pointer items-center justify-center gap-2.5 rounded-full bg-rail px-6 text-[15px] font-semibold text-white shadow-[0_6px_16px_rgba(29,102,255,0.30)] transition-colors hover:brightness-95"
      >
        <SquarePen size={18} strokeWidth={2} aria-hidden="true" />
        Escrever
      </button>

      {carregando ? (
        <p className="flex items-center gap-2 px-2.5 py-2 text-[12px] text-tertiary">
          <RefreshCw size={14} className="animate-spin motion-reduce:animate-none" />
          Carregando pastas
        </p>
      ) : (
        <>
          <Rotulo>Favoritos</Rotulo>
          <ul className="flex flex-col gap-0.5">
            {favoritos.map((r) => (
              <ItemDePasta
                key={r.pasta.id}
                item={r}
                ativo={pastaAtiva(r.pasta.id)}
                onClick={() => selectFolder(r.pasta.id)}
                acoes={acoes?.(r.pasta)}
                onMenu={
                  accountId === null ? undefined : (ponto) => setMenu({ pasta: r.pasta, ponto })
                }
              />
            ))}
            {rascunhosVirtual && (
              <li>
                <button
                  type="button"
                  onClick={abrirRascunhos}
                  aria-current={verRascunhos ? 'page' : undefined}
                  className={cn(
                    'flex h-10 w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-left transition-colors duration-[var(--duration-micro)]',
                    verRascunhos ? 'bg-rail text-white' : 'text-primary hover:bg-hover',
                  )}
                >
                  <PenLine
                    size={18}
                    strokeWidth={1.5}
                    aria-hidden="true"
                    className={verRascunhos ? 'text-white' : 'text-tertiary'}
                  />
                  <span className="text-[13px] font-semibold">Rascunhos</span>
                </button>
              </li>
            )}
          </ul>

          <button
            type="button"
            className="mt-2 flex h-9 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-left text-[13px] font-medium text-secondary hover:bg-hover"
          >
            <Star size={18} strokeWidth={1.5} aria-hidden="true" className="text-tertiary" />
            Adicionar favorito
          </button>

          <div className="mt-5 flex items-center justify-between pr-1">
            <Rotulo className="pb-0">Pastas</Rotulo>
            {accountId !== null && (
              <button
                type="button"
                aria-label="Nova pasta"
                title="Nova pasta"
                onClick={() => setPedido({ tipo: 'criar', paiId: null })}
                className="flex size-7 cursor-pointer items-center justify-center rounded-md text-tertiary hover:bg-hover hover:text-primary"
              >
                <Plus size={16} strokeWidth={1.5} aria-hidden="true" />
              </button>
            )}
          </div>
          {demais.length > 0 && (
            <ul className="mt-1.5 flex flex-col gap-0.5">
              {demais.map((r) => (
                <ItemDePasta
                  key={r.pasta.id}
                  item={r}
                  ativo={pastaAtiva(r.pasta.id)}
                  onClick={() => selectFolder(r.pasta.id)}
                  acoes={acoes?.(r.pasta)}
                  onMenu={
                    accountId === null ? undefined : (ponto) => setMenu({ pasta: r.pasta, ponto })
                  }
                />
              ))}
            </ul>
          )}
        </>
      )}

      {menu !== null && (
        <MenuDeContexto
          ponto={menu.ponto}
          rotulo={`Menu da pasta ${menu.pasta.name}`}
          onFechar={() => setMenu(null)}
        >
          <ItensDaPasta
            pasta={menu.pasta}
            onPedir={setPedido}
            onAbrir={() => selectFolder(menu.pasta.id)}
          />
        </MenuDeContexto>
      )}

      {accountId !== null && pedido !== null && (
        <Suspense fallback={null}>
          <DialogoDePasta accountId={accountId} pedido={pedido} onFechar={() => setPedido(null)} />
        </Suspense>
      )}

      <PastasInteligentes accountId={accountId} />

      <Acompanhamento />

      <BuscasSalvas accountId={accountId} />

      <Rotulo className="mt-5">Contas</Rotulo>
      <EstadoDasContas />
      <button
        type="button"
        onClick={onAdicionarConta}
        className="flex h-9 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-left text-[13px] font-medium text-secondary hover:bg-hover"
      >
        <Plus size={18} strokeWidth={1.5} aria-hidden="true" className="text-tertiary" />
        Adicionar caixa
      </button>
    </nav>
  );
}

function Rotulo({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={cn('px-2.5 pb-1.5 text-[12px] font-semibold text-tertiary', className)}>
      {children}
    </p>
  );
}

function ItemDePasta({
  item,
  ativo,
  onClick,
  acoes,
  onMenu,
}: {
  item: PastaRotulada;
  ativo: boolean;
  onClick: () => void;
  /** Menu da pasta, ao lado do botao: botao dentro de botao nao e HTML valido. */
  acoes?: React.ReactNode;
  /** Botao direito ou Shift+F10 sobre a pasta. */
  onMenu?: ((ponto: PontoDoMenu) => void) | undefined;
}) {
  const { pasta, prefixo, folha, caminho } = item;
  const Icon = ICONES[pasta.role ?? ''] ?? Inbox;

  return (
    <li
      className="group/pasta relative flex items-center"
      onContextMenu={(e) => {
        if (onMenu === undefined) return;
        e.preventDefault();
        onMenu(pontoDoEvento(e, e.currentTarget));
      }}
      onKeyDown={(e) => {
        if (onMenu === undefined || !ehTeclaDeMenu(e)) return;
        e.preventDefault();
        onMenu(pontoDoElemento(e.currentTarget));
      }}
    >
      <button
        type="button"
        onClick={onClick}
        aria-current={ativo ? 'page' : undefined}
        // O rotulo pode ser curto ou truncado; o caminho inteiro fica aqui.
        title={caminho}
        className={cn(
          'flex h-10 w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-left transition-colors duration-[var(--duration-micro)]',
          ativo ? 'bg-rail text-white' : 'text-primary hover:bg-hover',
        )}
      >
        <Icon
          size={18}
          strokeWidth={1.5}
          aria-hidden="true"
          className={ativo ? 'text-white' : 'text-tertiary'}
        />
        {/*
          O contexto encolhe, o nome da pasta nao. Com o rotulo inteiro num
          `truncate` so, tres pastas de cPanel que so diferiam no fim viravam
          tres linhas identicas na tela.
        */}
        <span className="flex min-w-0 flex-1 items-baseline text-[13px] font-semibold">
          {prefixo !== '' && <span className="truncate text-tertiary">{prefixo}</span>}
          <span className="shrink-0">{folha}</span>
        </span>

        {pasta.unreadCount > 0 && (
          <span className="flex min-w-5 shrink-0 items-center justify-center rounded-full bg-badge px-1.5 text-[11px] font-bold text-white dark:text-wc-dark">
            {pasta.unreadCount}
          </span>
        )}
      </button>
      {/* Por cima do contador, so no hover ou no foco: a lista fica limpa. */}
      {acoes !== undefined && (
        <span className="absolute right-1 rounded-md bg-raised opacity-0 group-hover/pasta:opacity-100 focus-within:opacity-100 has-[[data-state=open]]:opacity-100">
          {acoes}
        </span>
      )}
    </li>
  );
}
